import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePortal } from "../../classes/contexts/PortalContext";
import "./Tooltip.css";

export const TooltipOrientation = {
  Top: "top",
  Bottom: "bottom",
  Left: "left",
  Right: "right",
};

const TIMEOUT_MS = 400;

const Tooltip = ({
  children,
  text,
  content,
  orientation = TooltipOrientation.Top,
  // Allow overriding portal target; by default we use the app's portal root if available
  portalTarget = null,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  const scrollParentsRef = useRef([]);
  // Prefer the app-level portal root to keep theme and stacking context consistent with popups
  const portalRoot = usePortal();
  const effectivePortalTarget =
    portalTarget ||
    portalRoot ||
    (typeof document !== "undefined" ? document.body : null);

  const updatePosition = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const offset = 10;

    let top, left;

    switch (orientation) {
      case TooltipOrientation.Top:
        top = rect.top - offset;
        left = rect.left + rect.width / 2;
        break;
      case TooltipOrientation.Bottom:
        top = rect.bottom + offset;
        left = rect.left + rect.width / 2;
        break;
      case TooltipOrientation.Left:
        top = rect.top + rect.height / 2;
        left = rect.left - offset;
        break;
      case TooltipOrientation.Right:
        top = rect.top + rect.height / 2;
        left = rect.right + offset;
        break;
      default:
        top = rect.top - offset;
        left = rect.left + rect.width / 2;
    }

    setTooltipPosition({ top, left });
  }, [orientation]);

  const getScrollParents = node => {
    const parents = [];
    if (!node) return parents;
    let parent = node.parentElement;
    const overflowRegex = /(auto|scroll|overlay)/;

    while (parent) {
      const style = getComputedStyle(parent);
      const overflow = `${style.overflow}${style.overflowY}${style.overflowX}`;
      if (overflowRegex.test(overflow)) {
        parents.push(parent);
      }
      parent = parent.parentElement;
    }
    // Also listen to window for viewport scroll/resize
    parents.push(window);
    return parents;
  };

  const hoverTimeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, TIMEOUT_MS);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setShowTooltip(false);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showTooltip) return;

    // Initial calculation
    updatePosition();

    // Subscribe to scroll on all scrollable ancestors and to window resize
    const parents = getScrollParents(wrapperRef.current);
    scrollParentsRef.current = parents;
    parents.forEach(p =>
      p.addEventListener("scroll", updatePosition, { passive: true })
    );
    window.addEventListener("resize", updatePosition, { passive: true });

    // Also recalc on animation frame while visible to handle edge transforms
    let rafId;
    const tick = () => {
      updatePosition();
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      parents.forEach(p => p.removeEventListener("scroll", updatePosition));
      window.removeEventListener("resize", updatePosition);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [showTooltip, orientation, updatePosition]);

  // Support both text (string) and content (JSX) for backwards compatibility
  const tooltipContent = content || text;
  // Determine if content is JSX (not a string)
  const isJSXContent = content !== undefined && content !== null;

  return (
    <div
      ref={wrapperRef}
      className="App-tooltip-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {showTooltip && effectivePortalTarget
        ? createPortal(
            <div
              className={`App-tooltip-content App-text-small ${orientation} ${
                isJSXContent ? "jsx-content" : ""
              }`}
              style={{
                top: `${tooltipPosition.top}px`,
                left: `${tooltipPosition.left}px`,
                transform:
                  orientation === TooltipOrientation.Top
                    ? "translateX(-50%) translateY(-100%)"
                    : orientation === TooltipOrientation.Bottom
                    ? "translateX(-50%)"
                    : orientation === TooltipOrientation.Left
                    ? "translateX(-100%) translateY(-50%)"
                    : orientation === TooltipOrientation.Right
                    ? "translateY(-50%)"
                    : "none",
              }}
            >
              {tooltipContent}
            </div>,
            effectivePortalTarget
          )
        : showTooltip && (
            <div
              className={`App-tooltip-content App-text-small ${orientation} ${
                isJSXContent ? "jsx-content" : ""
              }`}
              style={{
                top: `${tooltipPosition.top}px`,
                left: `${tooltipPosition.left}px`,
                transform:
                  orientation === TooltipOrientation.Top
                    ? "translateX(-50%) translateY(-100%)"
                    : orientation === TooltipOrientation.Bottom
                    ? "translateX(-50%)"
                    : orientation === TooltipOrientation.Left
                    ? "translateX(-100%) translateY(-50%)"
                    : orientation === TooltipOrientation.Right
                    ? "translateY(-50%)"
                    : "none",
              }}
            >
              {tooltipContent}
            </div>
          )}
    </div>
  );
};

export default Tooltip;
